import { IconFileText } from "central-icons/IconFileText";
import { IconHeartBeat } from "central-icons/IconHeartBeat";
import type React from "react";
import { useCallback, useRef, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { IconCode } from "@/assets/icons/geist/IconCode";
import { IconEdit } from "@/assets/icons/geist/IconEdit";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { IconOnaCopy } from "@/assets/icons/IconOnaCopy";
import { IconOnaDownload } from "@/assets/icons/IconOnaDownload";
import { IconOnaFolder } from "@/assets/icons/IconOnaFolder";
import { withGitpodSize } from "@/assets/icons/utils";
import { EnvironmentLifetimeExceededModal } from "@/components/environments/EnvironmentLifetimeExceededModal";
import { ResourceUsageDialog } from "@/components/environments/ResourceUsageDialog";
import {
    ContextMenuItem,
    ContextMenuRoot,
    ContextMenuTrigger,
    ContextMenuPortal,
    ContextMenuContent,
    ContextMenuSeparator,
} from "@/components/flexkit/ContextMenu";
import { useToast } from "@/components/flexkit/toast";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useCanStartEnvironment } from "@/hooks/use-can-start-environment";
import { useCompleteSupportBundle } from "@/hooks/use-complete-support-bundle";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useIsPointerCoarse } from "@/hooks/use-is-pointer-coarse";
import { useLifetimeEnforcement } from "@/hooks/use-lifetime-enforcement";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useAgentExecutionsForEnvironment } from "@/queries/agent-queries";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useStartEnvironment, useStopEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { useRunner } from "@/queries/runner-queries";
import { DeleteEnvironmentModal, ForceDeleteEnvironmentModal } from "@/routes/environments/DeleteEnvironmentModal";
import { canOpen, showStart, showStop } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";

const IconFileTextSized = withGitpodSize(IconFileText);
import { IconConsole } from "central-icons/IconConsole";

const IconHeartBeatSized = withGitpodSize(IconHeartBeat);
const IconConsoleSized = withGitpodSize(IconConsole);

type EnvironmentActionsMenuProps = {
    environment: PlainEnvironment;
    handleViewDetails?: () => void;
    handleGoToProject?: () => void;
    startRename: (() => void) | undefined;
    children: React.ReactNode;
    renderAs: "dropdownMenu" | "contextMenu";
    onOpenChange?: (open: boolean) => void;
    agentExecution?: PlainAgentExecution;
    open?: boolean;
};

export const EnvironmentActionsMenu: FC<EnvironmentActionsMenuProps> = ({
    environment,
    handleViewDetails,
    handleGoToProject,
    renderAs,
    children,
    startRename,
    onOpenChange,
    agentExecution,
    open,
}) => {
    const pendingRenameRef = useRef(false);

    const isPointerCoarse = useIsPointerCoarse();

    const Root = renderAs === "dropdownMenu" ? DropdownMenu : ContextMenuRoot;
    const Trigger = renderAs === "dropdownMenu" ? DropdownMenuTrigger : ContextMenuTrigger;

    // Modal state lives here so modals survive menu close (Content unmounts on close)
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
    const [showResourceUsage, setShowResourceUsage] = useState(false);
    const { handleStartError, modalProps: lifetimeModalProps } = useLifetimeEnforcement();

    // On touch devices, disable the Radix context menu trigger to prevent its
    // built-in 700ms long-press from firing during scroll gestures and locking
    // the page. The dropdown menu (via react-aria useLongPress) handles mobile
    // long-press instead.
    const disableContextTrigger = renderAs === "contextMenu" && isPointerCoarse;

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            onOpenChange?.(isOpen);
            if (!isOpen && pendingRenameRef.current) {
                startRename?.();
            }
        },
        [onOpenChange, startRename],
    );

    const handleCloseAutoFocus = useCallback(
        (event: Event) => {
            if (!pendingRenameRef.current) {
                return;
            }
            // Renaming swaps the menu trigger out for an inline form. If Radix
            // restores focus to the old trigger, the editor blurs and closes.
            event.preventDefault();
            pendingRenameRef.current = false;
        },
        [],
    );

    const Portal = renderAs === "dropdownMenu" ? "div" : ContextMenuPortal; // dropdownMenu already has a portal
    const Content = renderAs === "dropdownMenu" ? DropdownMenuContent : ContextMenuContent;

    return (
        <>
            <Root modal={true} onOpenChange={handleOpenChange} open={open}>
                {renderAs === "dropdownMenu" ? (
                    <Trigger asChild>{children}</Trigger>
                ) : (
                    <Trigger disabled={disableContextTrigger}>{children}</Trigger>
                )}
                <Portal>
                    {/* DropdownMenuItem has mx-1 built-in, ContextMenuItem doesn't - use px-0 for dropdown to match */}
                    <Content
                        className={cn("min-w-[220px] rounded-md bg-surface-01 p-1 shadow-lg", {
                            "px-0 py-1": renderAs === "dropdownMenu",
                        })}
                        onCloseAutoFocus={handleCloseAutoFocus}
                    >
                        <EnvironmentActionsMenuContent
                            environment={environment}
                            handleViewDetails={handleViewDetails}
                            handleGoToProject={handleGoToProject}
                            renderAs={renderAs}
                            pendingRenameRef={pendingRenameRef}
                            onShowDelete={() => setShowDeleteModal(true)}
                            onShowForceDelete={() => setShowForceDeleteModal(true)}
                            onShowResourceUsage={() => setShowResourceUsage(true)}
                            handleStartError={handleStartError}
                        />
                    </Content>
                </Portal>
            </Root>
            {/* Modals are siblings of Root so they survive menu close */}
            {showDeleteModal && (
                <DeleteEnvironmentModal
                    environment={environment}
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    agentExecution={agentExecution}
                />
            )}
            {showForceDeleteModal && (
                <ForceDeleteEnvironmentModal
                    environment={environment}
                    open={showForceDeleteModal}
                    onClose={() => setShowForceDeleteModal(false)}
                    agentExecution={agentExecution}
                />
            )}
            <EnvironmentLifetimeExceededModal environment={environment} {...lifetimeModalProps} />
            {showResourceUsage && (
                <ResourceUsageDialog
                    environmentId={environment.id}
                    open={showResourceUsage}
                    onOpenChange={setShowResourceUsage}
                />
            )}
        </>
    );
};

const EnvironmentActionsMenuContent: FC<
    Omit<EnvironmentActionsMenuProps, "children" | "onOpenChange" | "open" | "startRename" | "agentExecution"> & {
        pendingRenameRef: React.MutableRefObject<boolean>;
        onShowDelete: () => void;
        onShowForceDelete: () => void;
        onShowResourceUsage: () => void;
        handleStartError: (error: unknown) => boolean;
    }
> = ({
    environment,
    handleViewDetails,
    handleGoToProject,
    renderAs,
    pendingRenameRef,
    onShowDelete,
    onShowForceDelete,
    onShowResourceUsage,
    handleStartError,
}) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const canStartEnvironment = useCanStartEnvironment(environment);
    const { selectedEditor: defaultEditor } = useGetEditorForEnv(environment.id, environment.metadata?.projectId);

    const { data: runner } = useRunner(environment.metadata?.runnerId);
    const { data: allExecutions } = useAgentExecutionsForEnvironment(environment.id);
    const { downloadSupportBundle, isSupportBundleAvailable, enableTokenFetch, isTokenReady } =
        useCompleteSupportBundle(environment, allExecutions);

    const allowForceDelete = runner && runner.provider !== RunnerProvider.MANAGED;

    const handleViewLogs = useCallback(() => {
        navigate(`/details/${environment.id}/logs`);
    }, [environment.id, navigate]);

    const handleStart = useCallback(async () => {
        try {
            await startEnvironment.mutateAsync({
                environmentId: environment.id,
                skipPolicyCheck: true,
            });
        } catch (error) {
            if (handleStartError(error)) {
                return;
            }
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        }
    }, [startEnvironment, environment.id, handleStartError, toast]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(environment.id);
        toast({
            title: `Environment ID copied to clipboard`,
            description: environment.id,
        });
    }, [toast, environment.id]);

    const openEditor = useOpenEditor();
    const handleStop = useCallback(async () => {
        try {
            await stopEnvironment.mutateAsync(environment.id);
        } catch (error) {
            toast({
                title: "Failed to stop environment",
                description: formatError(error),
            });
        }
    }, [stopEnvironment, toast, environment.id]);

    const handleRenameSelect = useCallback(() => {
        pendingRenameRef.current = true;
    }, [pendingRenameRef]);

    // Trigger token fetch immediately on mount (menu just opened)
    useState(() => enableTokenFetch());

    const MenuItem = renderAs === "dropdownMenu" ? DropdownMenuItem : ContextMenuItem;
    const Separator = renderAs === "dropdownMenu" ? DropdownMenuSeparator : ContextMenuSeparator;

    return (
        <>
                    {/* Open in editor */}
                    <MenuItem
                        onClick={() => defaultEditor && openEditor(defaultEditor, environment)}
                        disabled={!canOpen(environment)}
                        LeadingIcon={IconCode}
                        data-tracking-id="open-environment-environment-actions-menu"
                    >
                        Open in editor
                    </MenuItem>

                    <Separator />

                    {/* Section 1: Links / navigation */}
                    {handleViewDetails && (
                        <MenuItem
                            onClick={handleViewDetails}
                            LeadingIcon={IconFileTextSized}
                            data-tracking-id="view-environment-details-environment-actions-menu"
                        >
                            Details
                        </MenuItem>
                    )}
                    <MenuItem
                        onClick={handleViewLogs}
                        LeadingIcon={IconConsoleSized}
                        data-testid="environment-actions-dropdown-view-logs"
                        data-tracking-id="view-environment-logs-environment-actions-menu"
                    >
                        Logs
                    </MenuItem>
                    <MenuItem
                        onClick={onShowResourceUsage}
                        LeadingIcon={IconHeartBeatSized}
                        data-testid="environment-actions-menu-resource-usage"
                        data-tracking-id="resource-usage-environment-actions-menu"
                    >
                        Resource usage
                    </MenuItem>
                    <Tooltip
                        content={!handleGoToProject ? "No project associated with this environment" : undefined}
                    >
                        <div>
                            <MenuItem
                                onClick={handleGoToProject}
                                disabled={!handleGoToProject}
                                LeadingIcon={IconOnaFolder}
                                data-tracking-id="go-to-project-environment-actions-menu"
                            >
                                Go to project
                            </MenuItem>
                        </div>
                    </Tooltip>

                    <Separator />

                    {/* Section 2: Debug actions */}
                    <MenuItem
                        onClick={handleCopyId}
                        LeadingIcon={IconOnaCopy}
                        data-tracking-id="copy-environment-id-environment-actions-menu"
                    >
                        Copy ID
                    </MenuItem>
                    {isSupportBundleAvailable && (
                        <MenuItem
                            onClick={downloadSupportBundle}
                            disabled={!isTokenReady}
                            LeadingIcon={IconOnaDownload}
                            data-testid="environment-actions-menu-download-support-bundle"
                            data-tracking-id="download-support-bundle-environment-actions-menu"
                        >
                            Download support bundle
                        </MenuItem>
                    )}

                    <Separator />

                    {/* Section 3: Environment actions */}
                    {showStart(environment) && (
                        <MenuItem
                            onClick={handleStart}
                            disabled={!canStartEnvironment}
                            LeadingIcon={(props) => (
                                <IconPlay {...props} className={cn(props.className, "text-content-secondary")} />
                            )}
                            data-testid="environment-actions-dropdown-start"
                            data-tracking-id="start-environment-environment-actions-menu"
                        >
                            Start
                        </MenuItem>
                    )}
                    {showStop(environment) && (
                        <MenuItem
                            onClick={handleStop}
                            LeadingIcon={(props) => (
                                <IconStop {...props} className={cn(props.className, "text-content-secondary")} />
                            )}
                            data-testid="environment-actions-dropdown-stop"
                            data-tracking-id="stop-environment-environment-actions-menu"
                        >
                            Stop
                        </MenuItem>
                    )}
                    <MenuItem
                        onSelect={handleRenameSelect}
                        LeadingIcon={IconEdit}
                        data-tracking-id="rename-environment-environment-actions-menu"
                    >
                        Rename
                    </MenuItem>
                    {/* Section 4: Destructive */}
                    {environment.spec?.desiredPhase !== EnvironmentPhase.DELETED && (
                        <>
                            <Separator />
                            <MenuItem
                                variant="destructive"
                                onClick={onShowDelete}
                                LeadingIcon={IconTrash}
                                data-tracking-id="delete-environment-environment-actions-menu"
                            >
                                Delete
                            </MenuItem>
                        </>
                    )}
                    {environment.spec?.desiredPhase === EnvironmentPhase.DELETED && allowForceDelete && (
                        <>
                            <Separator />
                            <MenuItem
                                onClick={onShowForceDelete}
                                variant="destructive"
                                LeadingIcon={IconTrash}
                                data-tracking-id="force-delete-environment-environment-actions-menu"
                            >
                                Force delete
                            </MenuItem>
                        </>
                    )}
        </>
    );
};
